import React, { useState, useRef, useEffect, ChangeEvent } from 'react';
import { getEntityId } from 'helpers/entityId';
import { Form } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { GET_EMPLOYEES_QUERY } from 'services/api/employee';
import { Employee } from 'data/users';
import { MentionUser } from 'types/mention';

interface MentionInputProps {
  value: string;
  onChange: (value: string, mentionedUsers: MentionUser[]) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  disabled?: boolean;
}

const MentionInput: React.FC<MentionInputProps> = ({
  value,
  onChange,
  placeholder = 'Type @ to mention someone...',
  rows = 3,
  className = '',
  disabled = false
}) => {
  const [showMentionDropdown, setShowMentionDropdown] =
    useState<boolean>(false);
  const [mentionSearchTerm, setMentionSearchTerm] = useState<string>('');
  const [mentionedUsers, setMentionedUsers] = useState<MentionUser[]>([]);
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const [mentionStartPosition, setMentionStartPosition] = useState<number>(0);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: employeesData } = useQuery(
    'employees_for_mentions',
    GET_EMPLOYEES_QUERY,
    {
      staleTime: 5 * 60 * 1000,
      select: (response: any) => response?.data || []
    }
  );

  const filteredEmployees =
    employeesData?.filter((employee: Employee) =>
      `${employee.firstName} ${employee.lastName}`
        .toLowerCase()
        .includes(mentionSearchTerm.toLowerCase())
    ) || [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowMentionDropdown(false);
        setHighlightedIndex(0);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (value === '' && mentionedUsers.length > 0) {
      setMentionedUsers([]);
      onChange(value, []);
    }
  }, [value, mentionedUsers.length, onChange]);

  // Reset highlighted index when filtered employees change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredEmployees.length, mentionSearchTerm]);

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = e.target.value;
    const cursorPos = e.target.selectionStart || 0;

    setCursorPosition(cursorPos);

    const textBeforeCursor = inputValue.substring(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      setMentionSearchTerm(mentionMatch[1]);
      setMentionStartPosition(cursorPos - mentionMatch[0].length);
      setShowMentionDropdown(true);
    } else {
      setShowMentionDropdown(false);
      setMentionSearchTerm('');
      setHighlightedIndex(0);
    }

    updateMentionedUsersFromContent(inputValue);

    onChange(inputValue, mentionedUsers);
  };

  const updateMentionedUsersFromContent = (content: string) => {
    const mentionRegex = /@(\w+\s+\w+)/g;
    const matches = content.match(mentionRegex) || [];

    const currentMentions = matches
      .map(match => {
        const name = match.substring(1);
        return employeesData?.find(
          (emp: Employee) => `${emp.firstName} ${emp.lastName}` === name
        );
      })
      .filter(Boolean);

    const uniqueMentions = currentMentions.filter(
      (mention, index, self) =>
        self.findIndex(m => m?._id === mention?._id) === index
    );

    setMentionedUsers(
      uniqueMentions.map(emp => ({
        _id: getEntityId(emp) || '',
        firstName: emp.firstName,
        lastName: emp.lastName,
        profileImage: emp.profileImage,
        username: emp.username
      }))
    );
  };

  const handleMentionSelect = (employee: Employee) => {
    const mentionText = `@${employee.firstName} ${employee.lastName}`;
    const beforeMention = value.substring(0, mentionStartPosition);
    const afterMention = value.substring(cursorPosition);
    const newContent = beforeMention + mentionText + ' ' + afterMention;

    setShowMentionDropdown(false);
    setMentionSearchTerm('');
    setHighlightedIndex(0);

    const newMentionedUser = {
      _id: getEntityId(employee) || '',
      firstName: employee.firstName,
      lastName: employee.lastName,
      profileImage: employee.profileImage,
      username: employee.username
    };

    const isAlreadyMentioned = mentionedUsers.some(
      user => user._id === getEntityId(employee)
    );
    const updatedMentionedUsers = isAlreadyMentioned
      ? mentionedUsers
      : [...mentionedUsers, newMentionedUser];

    setMentionedUsers(updatedMentionedUsers);

    onChange(newContent, updatedMentionedUsers);

    if (textareaRef.current) {
      const newCursorPosition = mentionStartPosition + mentionText.length + 1;
      setTimeout(() => {
        textareaRef.current?.focus();
        textareaRef.current?.setSelectionRange(
          newCursorPosition,
          newCursorPosition
        );
      }, 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showMentionDropdown || filteredEmployees.length === 0) {
      if (e.key === 'Escape') {
        setShowMentionDropdown(false);
        setHighlightedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredEmployees.length - 1 ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : prev));
        break;

      case 'Tab':
      case 'Enter':
        e.preventDefault();
        if (filteredEmployees[highlightedIndex]) {
          handleMentionSelect(filteredEmployees[highlightedIndex]);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setShowMentionDropdown(false);
        setHighlightedIndex(0);
        break;

      default:
        break;
    }
  };

  const renderMentionDropdown = () => {
    if (!showMentionDropdown || filteredEmployees.length === 0) return null;

    return (
      <div
        ref={dropdownRef}
        className="position-absolute bg-white border rounded shadow-lg mention-dropdown"
        style={{
          top: '100%',
          left: '0',
          right: '0',
          zIndex: 1000,
          maxHeight: '200px',
          overflowY: 'auto'
        }}
      >
        {filteredEmployees
          .slice(0, 10)
          .map((employee: Employee, index: number) => (
            <div
              key={getEntityId(employee)}
              className={`d-flex align-items-center p-2 mention-item ${
                index === highlightedIndex ? 'bg-primary-subtle' : ''
              }`}
              onClick={() => handleMentionSelect(employee)}
              style={{
                cursor: 'pointer',
                backgroundColor:
                  index === highlightedIndex ? '#e7f3ff' : 'transparent'
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <div
                className="rounded-circle d-flex align-items-center justify-content-center me-2"
                style={{
                  width: '32px',
                  height: '32px',
                  backgroundColor: employee.profileImage
                    ? 'transparent'
                    : '#6c757d',
                  backgroundImage: employee.profileImage
                    ? `url(${employee.profileImage})`
                    : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
              >
                {!employee.profileImage &&
                  `${employee.firstName.charAt(0)}${employee.lastName.charAt(
                    0
                  )}`}
              </div>
              <div>
                <div className="fw-semibold">
                  {employee.firstName} {employee.lastName}
                </div>
                <div className="text-muted small">@{employee.username}</div>
              </div>
            </div>
          ))}
        <div className="p-2 border-top bg-light">
          <small className="text-muted">
            Use ↑↓ to navigate, Tab or Enter to select, Esc to close
          </small>
        </div>
      </div>
    );
  };

  return (
    <div className={`position-relative ${className}`}>
      <Form.Control
        as="textarea"
        rows={rows}
        ref={textareaRef}
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={disabled}
        onKeyDown={handleKeyDown}
      />
      {renderMentionDropdown()}

      {mentionedUsers.length > 0 && (
        <div className="mt-2">
          <small className="text-muted">Mentioning:</small>
          <div className="d-flex flex-wrap gap-1 mt-1">
            {mentionedUsers.map((user, index) => (
              <span
                key={index}
                className="badge bg-primary d-flex align-items-center"
                style={{ fontSize: '12px' }}
              >
                <div
                  className="rounded-circle me-1"
                  style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: user.profileImage ? 'transparent' : '#fff',
                    backgroundImage: user.profileImage
                      ? `url(${user.profileImage})`
                      : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    color: '#0d6efd',
                    fontSize: '8px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {!user.profileImage &&
                    `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`}
                </div>
                {user.firstName} {user.lastName}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MentionInput;
